import React from "react";
import { Box, Button, Divider, Paper, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const EFFECTIVE_DATE = "August 25, 2026";

const privacySections = [
  [
    "1. Scope",
    "This Privacy Policy explains how Enki RPO (the “Service,” “we,” “us,” or “our”) handles personal information when you use the Service. It applies to account holders, recruiting-team users, candidates, contacts, and visitors whose information is entered into or made available through the Service.",
  ],
  [
    "2. Information we process",
    "Depending on how the Service is used, this may include account and contact details; candidate and recruiting information; resumes and other documents; messages, notes, activities, and preferences; usage, device, and log information; and information supplied by authorized users or connected services.",
  ],
  [
    "3. How we use information",
    "We use information to provide, secure, support, and improve the Service; manage accounts; process recruiting workflows; communicate about the Service; prevent fraud and misuse; and meet legal obligations. We process customer-uploaded candidate and contact data on the customer’s behalf and under the customer’s instructions.",
  ],
  [
    "4. How information is shared",
    "We may share information with your authorized organization users, service providers that help operate the Service, professional advisers, and authorities where required by law. We do not sell personal information for money. We do not share personal information for cross-context behavioral advertising.",
  ],
  [
    "5. Data security and retention",
    "We use reasonable administrative, technical, and organizational measures designed to protect information. No method of transmission or storage is completely secure. We retain information for as long as needed to provide the Service, meet legal obligations, resolve disputes, and enforce agreements, unless a customer instructs us otherwise where applicable.",
  ],
  [
    "6. Your choices and privacy rights",
    "Depending on where you live, you may have rights to request access, correction, deletion, or portability of personal information, or to object to or limit certain processing. Submit a request through your organization’s account administrator or the in-app support channel. We may need to verify your request and may be unable to fulfill it where an exception applies.",
  ],
  [
    "7. International transfers",
    "Information may be processed in countries other than the country where it was collected. Where required, we use appropriate safeguards for such transfers.",
  ],
  [
    "8. Children",
    "The Service is not directed to children under 13, and we do not knowingly collect personal information from children under 13.",
  ],
  [
    "9. Changes and contact",
    "We may update this policy from time to time. We will post the updated version and revise the effective date. For privacy questions or requests, contact your organization’s account administrator or use the in-app support channel.",
  ],
];

const termsSections = [
  [
    "1. Acceptance and account access",
    "By accessing or using Enki RPO, you agree to these Terms of Service. If you use the Service on behalf of an organization, you represent that you have authority to bind that organization. You are responsible for keeping account credentials confidential and for activity under your account.",
  ],
  [
    "2. Permitted use",
    "Use the Service only for lawful recruiting and business purposes and in accordance with these Terms. You must have a lawful basis and all required notices or permissions to upload, access, or process candidate, contact, and other personal information through the Service.",
  ],
  [
    "3. Customer data",
    "You retain responsibility for Customer Data you submit to the Service, including its accuracy, legality, and the permissions needed to process it. You grant us a limited right to process Customer Data solely to provide, maintain, secure, and improve the Service and as otherwise permitted by these Terms.",
  ],
  [
    "4. Prohibited conduct",
    "You may not misuse the Service; violate privacy, employment, anti-discrimination, or other applicable laws; submit malicious code; attempt unauthorized access; interfere with the Service; scrape the Service except as expressly permitted; or use the Service to make decisions in a manner prohibited by law.",
  ],
  [
    "5. AI-assisted features",
    "The Service may provide AI-assisted suggestions, summaries, parsing, or workflow support. These outputs may be incomplete or inaccurate and must be reviewed by a qualified user before they are relied upon. You remain responsible for employment and business decisions made using the Service.",
  ],
  [
    "6. Intellectual property",
    "We and our licensors retain all rights in the Service, including its software, design, and content, excluding Customer Data. Subject to these Terms, we grant you a limited, non-exclusive, non-transferable right to use the Service during your authorized access period.",
  ],
  [
    "7. Availability and changes",
    "We may modify, suspend, or discontinue features when reasonably necessary to operate, secure, or improve the Service. We do not guarantee uninterrupted or error-free operation.",
  ],
  [
    "8. Disclaimers and liability",
    "To the maximum extent permitted by law, the Service is provided “as is” and “as available.” We disclaim warranties not expressly stated in these Terms. To the maximum extent permitted by law, neither party will be liable for indirect, incidental, special, consequential, or punitive damages. Nothing in these Terms excludes liability that cannot legally be excluded.",
  ],
  [
    "9. Termination and governing terms",
    "You may stop using the Service at any time. We may suspend or terminate access for material violations, security risks, or legal reasons. These Terms are governed by the terms of your organization’s applicable service agreement; where no separate agreement applies, applicable law will govern to the extent required by law.",
  ],
  [
    "10. Changes and contact",
    "We may update these Terms from time to time. Continued use after the effective date of an update means you accept the updated Terms to the extent permitted by law. For questions, contact your organization’s account administrator or use the in-app support channel.",
  ],
];

export default function LegalDocumentPage({ documentType }) {
  const isPrivacy = documentType === "privacy";
  const title = isPrivacy ? "Privacy Policy" : "Terms of Service";
  const sections = isPrivacy ? privacySections : termsSections;

  return (
    <Box sx={{ width: "100%", px: { xs: 1.5, md: 3 }, py: { xs: 2, md: 3 }, bgcolor: "#FFFFFF" }}>
      <Box sx={{ width: "100%" }}>
        <Typography sx={{ color: "#5B36F2", fontSize: 14, fontWeight: 700, mb: 0.75 }}>LEGAL</Typography>
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, border: "1px solid #E1E7F5", borderRadius: 2 }}>
          <Stack spacing={1.25} sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: "#102A5C", fontSize: { xs: "2rem", sm: "2.5rem" } }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">Effective date: {EFFECTIVE_DATE}</Typography>
            <Typography variant="body1" sx={{ color: "#344563", lineHeight: 1.7 }}>
              Please read this document carefully. It is a general product-policy template and should be reviewed by your legal counsel before publication.
            </Typography>
          </Stack>

          <Stack spacing={3}>
            {sections.map(([heading, body]) => (
              <Box key={heading}>
                <Typography variant="h6" sx={{ fontWeight: 750, color: "#102A5C", mb: 0.75 }}>{heading}</Typography>
                <Typography variant="body1" sx={{ color: "#344563", lineHeight: 1.7 }}>{body}</Typography>
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 4 }} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button component={RouterLink} to="/privacy-policy" variant={isPrivacy ? "contained" : "outlined"}>Privacy Policy</Button>
            <Button component={RouterLink} to="/terms-of-service" variant={!isPrivacy ? "contained" : "outlined"}>Terms of Service</Button>
            <Button component={RouterLink} to="/login" variant="text">Back to login</Button>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
